/**
 * 🎶 Up-next rail entries — the pure half of LofiRadio's rail (Nina's rulings
 * 08-15, built 08-18; TERMINAL_HANDOFF_2026_08_17.md §3.1).
 *
 * ⚠️ The order fed in must be the RESOLVED deck (LofiRadio's spun `deckIds`),
 * never the raw queue: when the shared clock cannot resolve, playback falls
 * back to a random rotation and a raw-queue rail would lie about what plays
 * next. First entry = what is playing NOW.
 *
 * ⚠️ Credit is not one field — house decks write `by`, /aux writes
 * `added_by_name`. One deck can hold both shapes; read both.
 *
 * The head track (data.music_url) may not be a queue entry (builder heads,
 * scene soundtracks) — `headTitle` (status `music_url_title`, backfilled by
 * `music_query` for houses) fills its title then.
 */
import { parseYouTubeUrl } from './lofiEmbed';

export function buildRailEntries(orderedIds, queueEntries, headId, headTitle) {
  if (!Array.isArray(orderedIds) || !orderedIds.length) return [];
  const byId = {};
  (Array.isArray(queueEntries) ? queueEntries : []).forEach((q) => {
    const p = parseYouTubeUrl(q?.url || '');
    if (!p || p.type !== 'video' || byId[p.id]) return;
    byId[p.id] = {
      title: q?.title || null,
      credit: q?.added_by_name || q?.by || null,
      // 🎤🖼️ 08-20 rail hybrid: artist line + adder avatar, stored on the
      // entry at add time (backend rider). Old entries: null → fallbacks.
      author: q?.author || null,
      by_avatar: q?.by_avatar || null,
      duration_s: Number(q?.duration_s) > 0 ? Number(q.duration_s) : null,
    };
  });
  return orderedIds.map((id) => ({
    id,
    title: byId[id]?.title || (id === headId ? (headTitle || null) : null),
    credit: byId[id]?.credit || null,
    author: byId[id]?.author || null,
    by_avatar: byId[id]?.by_avatar || null,
    duration_s: byId[id]?.duration_s ?? null,
  }));
}

/**
 * 🕰️ The room's needle, from the shared clock (08-20 rail hybrid). Walks the
 * RESOLVED deck order from `startedAtMs`, wrapping over the summed durations.
 * Returns { currentId, fraction } — fraction = progress through the current
 * track (0..1) — or null when the clock cannot resolve (any missing
 * duration, no start stamp): the caller hides the bar and keeps the static
 * order, exactly today's behavior. Pure: feed nowMs explicitly.
 *
 * ⚠️ This is the NOMINAL room position — the same arithmetic the backend
 * rebase uses — not a read of the embed. It can drift from a viewer's actual
 * player by buffering/ads; that is accepted (the bar is ambience, not a
 * scrubber).
 */
export function resolveDeckPosition(orderedIds, durations, startedAtMs, nowMs) {
  if (!Array.isArray(orderedIds) || !orderedIds.length) return null;
  const start = Number(startedAtMs);
  const now = Number(nowMs);
  if (!Number.isFinite(start) || !Number.isFinite(now) || now < start) return null;
  const durs = orderedIds.map((id) => Number(durations?.[id]));
  if (durs.some((d) => !Number.isFinite(d) || d <= 0)) return null;
  const total = durs.reduce((a, b) => a + b, 0);
  let pos = ((now - start) / 1000) % total;
  for (let i = 0; i < orderedIds.length; i++) {
    if (pos < durs[i]) return { currentId: orderedIds[i], fraction: pos / durs[i] };
    pos -= durs[i];
  }
  return { currentId: orderedIds[0], fraction: 0 }; // float edge: wrapped exactly
}

/**
 * 🧭 Rotate a deck order so headId leads (F5, preship #92). The shared-clock
 * anchor (jukebox_started_at) is defined over the HEAD-FIRST deck order, but
 * the caller's resolved deck is SPUN to the entry leader — walking the spun
 * order from the anchor returns a track shifted AHEAD by the summed durations
 * of everything before that leader. Rebase before every anchored walk.
 * Unknown/absent headId → order returned unchanged.
 */
export function rebaseDeckToHead(orderedIds, headId) {
  if (!Array.isArray(orderedIds) || !orderedIds.length) return orderedIds || [];
  const at = orderedIds.indexOf(headId);
  return at > 0 ? [...orderedIds.slice(at), ...orderedIds.slice(0, at)] : orderedIds;
}

/** 754 → "12:34"; null/0 → null (caller omits the chip). */
export function formatTrackDuration(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}
